import React from "react";
import {
    ResponsiveTable,
    THead,
    TBody,
    TR,
    TH,
    TD,
    Button,
    colors,
    CheckmarkCircleIcon,
} from "@commonsku/styles";
import { CatalogItem, CatalogItemsSortField } from "../../../types";
import type { CatalogItemsFilters } from "./CatalogPage";
import { CatalogSearchSupplierFilters } from "./CatalogSearchSupplierFilters";
import {
    CatalogItemsLoadMoreRow,
    CatalogProductActionsBar,
    CatalogProductCell,
    CatalogSupplierCell,
    EmptyItemStateComponent,
    PRIMARY_CELL_STYLE,
    TABLE_PARENT_PROPS,
    THSorted,
    getCatalogItemsSortOrder,
    EmptyStateWrapper,
    IconCircle,
    EmptyStateHeaderText,
    EmptyStateBodyText,
} from "./catalogShared";
import { CatalogUnpublishPopup } from "./CatalogUnpublishPopup";
import { useUnpublishCatalogPopup } from "./useUnpublishCatalogPopup";

interface CatalogPublishedViewProps {
    items: CatalogItem[];
    filters: CatalogItemsFilters;
    onFiltersChange: (patch: Partial<CatalogItemsFilters>) => void;
    onApplyFilters: () => void;
    onSortField: (field: CatalogItemsSortField) => void;
    onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    hasMore: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
    hasEverHadPublishedItems: boolean;
}

export const CatalogPublishedView = ({
    items,
    filters,
    onFiltersChange,
    onApplyFilters,
    onSortField,
    onSearchKeyDown,
    hasMore,
    loadingMore,
    onLoadMore,
    hasEverHadPublishedItems,
}: CatalogPublishedViewProps) => {
    const {
        item: unpublishItem,
        unpublishing,
        openUnpublishConfirm,
        onUnpublishConfirmCancel,
        onUnpublishConfirm,
    } = useUnpublishCatalogPopup();

    const sortOrder = (field: CatalogItemsSortField) =>
        getCatalogItemsSortOrder(filters.sort, field);

    if (!hasEverHadPublishedItems && items.length === 0) {
        return (
            <EmptyStateWrapper>
                <IconCircle>
                    <CheckmarkCircleIcon color={colors.neutrals[90]} />
                </IconCircle>
                <EmptyStateHeaderText>Your published products will live here</EmptyStateHeaderText>
                <EmptyStateBodyText>
                    Once you publish a product, it'll show up here and be available for sales reps to use.
                </EmptyStateBodyText>
            </EmptyStateWrapper>
        );
    }

    return (
        <>
            <CatalogSearchSupplierFilters
                filters={filters}
                onFiltersChange={onFiltersChange}
                onApplyFilters={onApplyFilters}
                onSearchKeyDown={onSearchKeyDown}
            />
            <CatalogProductActionsBar>
                Published products are locked. To make changes, set the product to Draft first.
            </CatalogProductActionsBar>
            <ResponsiveTable parentProps={TABLE_PARENT_PROPS}>
                <THead>
                    <TR>
                        <THSorted
                            padded
                            clickable
                            order={sortOrder("item_name")}
                            onClick={() => onSortField("item_name")}
                        >
                            Product
                        </THSorted>
                        <THSorted
                            padded
                            clickable
                            order={sortOrder("division_name")}
                            onClick={() => onSortField("division_name")}
                        >
                            Supplier
                        </THSorted>
                        <TH style={{ textAlign: "right" }} />
                    </TR>
                </THead>
                <TBody padded>
                    {items.length === 0 ? (
                        <TR>
                            <TD colSpan={3}>
                                <EmptyItemStateComponent />
                            </TD>
                        </TR>
                    ) : (
                        <>
                            {items.map((item) => (
                                <TR key={item.item_id}>
                                    <TD padded>
                                        <CatalogProductCell item={item} />
                                    </TD>
                                    <TD padded style={PRIMARY_CELL_STYLE}>
                                        <CatalogSupplierCell item={item} />
                                    </TD>
                                    <TD padded style={{ textAlign: "right" }}>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="medium"
                                            onClick={() => openUnpublishConfirm(item)}
                                        >
                                            Unpublish
                                        </Button>
                                    </TD>
                                </TR>
                            ))}
                            <CatalogItemsLoadMoreRow
                                colSpan={3}
                                hasMore={hasMore}
                                loading={loadingMore}
                                onLoadMore={onLoadMore}
                            />
                        </>
                    )}
                </TBody>
            </ResponsiveTable>
            <CatalogUnpublishPopup
                item={unpublishItem}
                unpublishing={unpublishing}
                onCancel={onUnpublishConfirmCancel}
                onConfirm={onUnpublishConfirm}
            />
        </>
    );
};
